/**
 * Job Hunter — create / edit one agent: its CV, roles, countries (with the visa
 * route each country uses), how strict it is and how much AI it may spend.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { Bot, ChevronDown, FileText, Globe, Save, SlidersHorizontal, Target, X } from 'lucide-react';
import { Button } from '@/components/Button';
import { CountryMultiPicker } from '@/components/CountryMultiPicker';
import { jobHunterService, type JhAgent, type JhAgentInput, type JhCv } from '@/services/job-hunter.service';
import { useJobHunter } from '@/lib/job-hunter/store';
import { AGENT_COLORS, agentHex, countryName, flag } from '@/lib/job-hunter/format';
import { Drawer, Field, Notice, TagInput, Toggle, ToggleRow, JhSelect } from './ui';
import { JH } from './JobHunterShell';

const lower = (s: string) => s.toLowerCase();

function Section({ icon, title, sub, children }: { icon: React.ReactNode; title: string; sub?: string; children: React.ReactNode }) {
  return (
    <section className="jh-card jh-card-pad">
      <div className="mb-3 flex items-start gap-2.5">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-primary-soft text-primary">{icon}</span>
        <div><div className="jh-card-title">{title}</div>{sub && <div className="jh-card-sub">{sub}</div>}</div>
      </div>
      {children}
    </section>
  );
}

export function AgentEditor({ open, agent, profileUid, cvs, onClose, onSaved }: {
  open: boolean;
  agent: JhAgent | null;
  profileUid: string;
  cvs: JhCv[];
  onClose: () => void;
  onSaved: (a: JhAgent) => void;
}) {
  const { meta } = useJobHunter();
  const defaults = meta?.defaults.agent;
  const [f, setF] = useState<JhAgentInput>({});
  const [advanced, setAdvanced] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAdvanced(false);
    setF(agent ? { ...agent } : { ...(defaults ?? {}), name: '', active: true, color: meta?.defaults.agentColors?.[0] ?? 'indigo', cvUid: undefined });
  }, [open, agent, defaults, meta?.defaults.agentColors]);

  const set = <K extends keyof JhAgentInput>(k: K, v: JhAgentInput[K]) => setF((x) => ({ ...x, [k]: v }));
  const targets = f.targetCountries ?? [];
  const routes = meta?.defaults.visaRoutes ?? {};
  const regions = meta?.defaults.regions ?? [];
  const defaultCv = cvs.find((c) => c.isDefault) ?? cvs[0];
  const colors = (meta?.defaults.agentColors?.length ? meta.defaults.agentColors : Object.keys(AGENT_COLORS)).filter((c) => c in AGENT_COLORS);

  const toggleRegion = (codes: string[]) => {
    const all = codes.every((c) => targets.includes(c));
    set('targetCountries', all ? targets.filter((c) => !codes.includes(c)) : Array.from(new Set([...targets, ...codes])));
  };
  const routeRows = targets.map((cc) => ({ cc, r: routes[cc] }));

  const save = async () => {
    if (!f.name?.trim()) { toast.error('Give the agent a name'); return; }
    if (!(f.roleKeywords ?? []).length) { toast.error('Add at least one role keyword'); return; }
    setSaving(true);
    try {
      const body: JhAgentInput = {
        name: f.name.trim(), color: f.color, active: f.active, cvUid: f.cvUid ?? '',
        roleKeywords: f.roleKeywords, searchQueries: f.searchQueries, excludeTitleKeywords: f.excludeTitleKeywords,
        sponsorshipPositive: f.sponsorshipPositive, sponsorshipNegative: f.sponsorshipNegative,
        targetCountries: f.targetCountries, excludedCountries: f.excludedCountries, acceptRemote: f.acceptRemote,
        jdLanguages: f.jdLanguages, requireSponsorship: f.requireSponsorship, maxAgeDays: f.maxAgeDays, minScore: f.minScore, dailyScoreLimit: f.dailyScoreLimit,
      };
      const saved = agent ? await jobHunterService.updateAgent(agent.agentUid, body) : await jobHunterService.createAgent({ ...body, profileUid });
      toast.success(agent ? 'Agent saved — it re-screens recent jobs on its next run' : 'Agent created — run it to screen the jobs already found');
      onSaved(saved);
      onClose();
    } catch { /* toasted */ } finally { setSaving(false); }
  };

  return (
    <Drawer open={open} onClose={onClose}>
      <div className="jh-drawer-head">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl text-white" style={{ background: agentHex(f.color) }}><Bot size={20} /></span>
            <div className="min-w-0">
              <div className="jh-eyebrow">{agent ? 'Edit agent' : 'New agent'}</div>
              <div className="truncate text-[17px] font-bold tracking-tight">{f.name?.trim() || 'Untitled agent'}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-text-secondary">{f.active ? 'Active' : 'Paused'}</span>
            <Toggle checked={!!f.active} onChange={(v) => set('active', v)} label="Active" />
            <button type="button" onClick={onClose} aria-label="Close" className="grid h-8 w-8 place-items-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-text-primary"><X size={17} /></button>
          </div>
        </div>
      </div>

      <div className="jh-drawer-body space-y-4">
        <Section icon={<Bot size={16} />} title="Name & CV" sub="Each agent scores jobs against its own CV and drafts applications with it.">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Agent name"><input className="jh-input" autoFocus={!agent} value={f.name ?? ''} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Flutter · EU & UK" maxLength={80} /></Field>
            <Field label="Colour">
              <div className="flex h-10 flex-wrap items-center gap-2">
                {colors.map((c) => (
                  <button key={c} type="button" aria-label={c} aria-pressed={f.color === c} onClick={() => set('color', c)}
                    className={clsx('h-7 w-7 rounded-full ring-offset-2 ring-offset-surface transition', f.color === c ? 'ring-2 ring-text-primary' : 'hover:scale-110')}
                    style={{ background: agentHex(c) }} />
                ))}
              </div>
            </Field>
            <Field label="CV this agent uses" className="sm:col-span-2" hint={cvs.length ? 'Pick the CV that fits these roles best. "Default CV" follows whichever CV is starred.' : undefined}>
              {cvs.length ? (
                <JhSelect value={f.cvUid ?? ''} onChange={(v) => set('cvUid', v || undefined)}
                  options={[
                    { value: '', label: 'Default CV', description: defaultCv ? `Follows the starred CV — now “${defaultCv.label}”` : 'Follows the starred CV' },
                    ...cvs.map((c) => ({ value: c.cvUid, label: c.label, description: [c.health?.score != null ? `health ${c.health.score}` : 'not reviewed', c.isDefault ? 'default' : ''].filter(Boolean).join(' · ') })),
                  ]} />
              ) : (
                <Notice tone="warning" title="This candidate has no CV yet" action={<Link to={JH.candidate(profileUid, 'cvs')}><Button size="sm" variant="secondary" iconLeft={<FileText size={13} />}>Add a CV</Button></Link>}>
                  Agents score jobs against the CV text. Without one, scores lean on the truth file only.
                </Notice>
              )}
            </Field>
          </div>
        </Section>

        <Section icon={<Target size={16} />} title="Roles" sub="Free filters that run before any AI call. A job must match a role keyword (in the title, or in the description of a technical title).">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Role keywords"><TagInput value={f.roleKeywords ?? []} onChange={(v) => set('roleKeywords', v)} transform={lower} placeholder="flutter, mobile engineer…" /></Field>
            <Field label="Search queries" hint="Sent to keyword boards (Himalayas, Adzuna). At most 12."><TagInput value={f.searchQueries ?? []} onChange={(v) => set('searchQueries', v)} transform={lower} max={12} placeholder="flutter developer…" /></Field>
          </div>
        </Section>

        <Section icon={<Globe size={16} />} title="Countries" sub="Where this agent looks. Tap a region to add or remove it — the visa route each country uses is shown below.">
          <div className="mb-3 flex flex-wrap gap-1.5">
            {regions.map((r) => {
              const on = r.codes.every((c) => targets.includes(c));
              return (
                <button key={r.key} type="button" onClick={() => toggleRegion(r.codes)} aria-pressed={on}
                  className={clsx('jh-chip transition', on ? '!bg-primary !text-on-primary' : 'hover:!bg-primary-soft hover:!text-primary')}>
                  {r.codes.slice(0, 3).map(flag).join('')} {r.label}
                </button>
              );
            })}
          </div>
          <CountryMultiPicker value={targets} onChange={(v) => set('targetCountries', v)} placeholder="Pick target countries…" />
          {routeRows.length > 0 && (
            <div className="mt-3 max-h-64 overflow-y-auto rounded-xl border border-border">
              {routeRows.map(({ cc, r }) => (
                <div key={cc} className="flex items-start gap-2.5 border-b border-border px-3 py-2 text-[12.5px] last:border-b-0">
                  <span className="text-[16px] leading-5">{flag(cc)}</span>
                  <div className="min-w-0">
                    <div><b>{countryName(cc)}</b>{r ? <span className="text-text-secondary"> — {r.route}</span> : <span className="text-text-muted"> — no route on file</span>}</div>
                    {r && <div className="text-[11.5px] text-text-muted">{r.note}{r.register ? ` Checked against the ${r.register}.` : ''}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <Field label="Never these countries"><CountryMultiPicker value={f.excludedCountries ?? []} onChange={(v) => set('excludedCountries', v)} placeholder="None" /></Field>
            <div className="space-y-3">
              <ToggleRow title="Accept remote jobs" hint="Remote roles open to these countries or worldwide." checked={!!f.acceptRemote} onChange={(v) => set('acceptRemote', v)} />
              <ToggleRow title="Only jobs that sponsor" hint="Propose only when sponsorship is explicit or likely." checked={!!f.requireSponsorship} onChange={(v) => set('requireSponsorship', v)} />
            </div>
          </div>
        </Section>

        <Section icon={<SlidersHorizontal size={16} />} title="Bar & budget" sub="How strict the agent is and how many AI scorings it may spend per day.">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={`Minimum fit to propose: ${f.minScore ?? 70}`}><input type="range" className="jh-range" min={40} max={95} value={f.minScore ?? 70} onChange={(e) => set('minScore', Number(e.target.value))} /></Field>
            <Field label="AI scorings per day"><input className="jh-input" type="number" min={0} max={500} value={f.dailyScoreLimit ?? 40} onChange={(e) => set('dailyScoreLimit', Number(e.target.value) || 0)} /></Field>
            <Field label="Description languages" hint="Two-letter codes. A German-only description usually means a German-speaking job."><TagInput value={f.jdLanguages ?? []} onChange={(v) => set('jdLanguages', v)} transform={(s) => s.toLowerCase().slice(0, 2)} /></Field>
            <Field label="Ignore jobs older than (days)"><input className="jh-input" type="number" min={1} max={60} value={f.maxAgeDays ?? 21} onChange={(e) => set('maxAgeDays', Number(e.target.value) || 21)} /></Field>
          </div>
        </Section>

        <section className="jh-card">
          <button type="button" className="flex w-full items-center justify-between gap-2 px-[18px] py-3.5 text-left" onClick={() => setAdvanced((v) => !v)} aria-expanded={advanced}>
            <span className="jh-card-title">Advanced — title and sponsorship words</span>
            <ChevronDown size={16} className={clsx('text-text-muted transition', advanced && 'rotate-180')} />
          </button>
          {advanced && (
            <div className="grid gap-4 border-t border-border p-[18px] sm:grid-cols-2">
              <Field label="Exclude titles containing" className="sm:col-span-2"><TagInput value={f.excludeTitleKeywords ?? []} onChange={(v) => set('excludeTitleKeywords', v)} transform={lower} /></Field>
              <Field label="Sponsorship words (raise in the AI queue)"><TagInput value={f.sponsorshipPositive ?? []} onChange={(v) => set('sponsorshipPositive', v)} transform={lower} /></Field>
              <Field label="No-sponsorship phrases (drop before AI)" hint="Unless the posting also clearly offers sponsorship — then the AI reads both."><TagInput value={f.sponsorshipNegative ?? []} onChange={(v) => set('sponsorshipNegative', v)} transform={lower} /></Field>
            </div>
          )}
        </section>
      </div>

      <div className="jh-drawer-foot justify-end">
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button loading={saving} iconLeft={<Save size={14} />} onClick={save}>{agent ? 'Save agent' : 'Create agent'}</Button>
      </div>
    </Drawer>
  );
}
